import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Tooltip
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, ControlsContainer } from '../../common/styles';
import {
  MainContainer,
  SkillsGrid,
  SkillCard,
  SkillCardContent,
  SkillTitle,
  EmployeeBadge,
  ActionButtons,
  UsageBadgesContainer,
  UsageBadge,
  CreatedAtText
} from './styles';
import { COLORS } from '../../../constants/radar';
import { useAlert } from '../../../contexts/AlertContext';
import { cvManagementApi } from '../../../services/api';
import ProjectSearch from '../../common/ProjectSearch';
import { getRelativeTime } from '../../../utils/dateUtils';
import { PROJECT_PURPOSE_TYPES } from '../../../constants/projectPurposes';
import LoadingSpinner from '../../common/LoadingSpinner';

const CustomProjectsReconciliation = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [customProjects, setCustomProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProject, setCurrentProject] = useState({ id: null, name: '' });
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);

  // Load custom projects on component mount
  useEffect(() => {
    fetchCustomProjects();
  }, []);

  const fetchCustomProjects = async () => {
    try {
      setLoading(true);
      const response = await cvManagementApi.getCustomProjects();
      setCustomProjects(response || []);
    } catch (error) {
      console.error('Error fetching custom projects:', error);
      showAlert('Failed to load custom projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      setCurrentProject({
        id: project.id,
        name: project.name
      });
      setSelectedProject(null);
    } else {
      setCurrentProject({ id: null, name: '' });
      setSelectedProject(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProject(null);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
  };

  const handleReconcileProject = async () => {
    if (!selectedProject) {
      showAlert('Please select a project to reconcile with', 'error');
      return;
    }

    try {
      // Call the API to reconcile the custom project with the selected project
      await cvManagementApi.reconcileCustomProject(
        currentProject.id,
        selectedProject.id
      );

      showAlert('Project reconciled successfully', 'success');

      // Refresh projects list
      await fetchCustomProjects();
      handleCloseDialog();
    } catch (error) {
      console.error('Error reconciling project:', error);
      showAlert(`Failed to reconcile project: ${error.message}`, 'error');
    }
  };

  // Filter projects based on search term
  const filteredProjects = customProjects.filter(project =>
    project.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    (project.cv && project.cv.fullName.toLowerCase().includes((searchTerm || '').toLowerCase()))
  );

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Custom Projects Reconciliation</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Projects"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>
          </Box>
        </ControlsContainer>

        <MainContainer>
          {!loading && (
            <div style={{
              color: '#64748B',
              fontSize: '14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontWeight: 600 }}>
                {filteredProjects.length}
              </span>
              {filteredProjects.length === 1 ? 'project' : 'projects'} found
            </div>
          )}

          {loading ? (
            <LoadingSpinner message="Loading projects..." />
          ) : filteredProjects.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px dashed #dee2e6'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#6c757d',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                No projects found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#adb5bd',
                  maxWidth: '400px'
                }}
              >
                {searchTerm ?
                  "No projects match your search criteria. Try adjusting your search term." :
                  "No custom projects have been added yet."}
              </Typography>
            </Box>
          ) : (
            <SkillsGrid>
              {filteredProjects.map((project) => (
                <SkillCard key={project.id}>
                  <SkillCardContent>
                    <SkillTitle>
                      {project.name}
                      <UsageBadgesContainer>
                        <UsageBadge>
                          <WorkIcon /> Employment History
                        </UsageBadge>
                      </UsageBadgesContainer>
                    </SkillTitle>

                    {project.cv && (
                      <EmployeeBadge>
                        <PersonIcon fontSize="small" /> {project.cv.fullName}
                      </EmployeeBadge>
                    )}

                    {project.createdAt && (
                      <CreatedAtText>
                        <AccessTimeIcon /> Added {getRelativeTime(project.createdAt)}
                      </CreatedAtText>
                    )}

                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(project)}
                        title="Reconcile with existing projects"
                      >
                        <LinkIcon />
                      </IconButton>
                    </ActionButtons>
                  </SkillCardContent>
                </SkillCard>
              ))}
            </SkillsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Reconcile Project Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: { xs: '95%', sm: '80%', md: '800px' },
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle>Reconcile Custom Project</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Reconcile the custom project <strong>"{currentProject.name}"</strong> with an existing project in the system.
          </DialogContentText>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }}>
              Select a Project to Reconcile With
            </Typography>
            <ProjectSearch
              value={selectedProject}
              onChange={handleProjectSelect}
              placeholder="Search for a project"
              width={400}
              sx={{ width: '100%' }}
              projectPurpose={PROJECT_PURPOSE_TYPES.DELIVERY_PROJECT}
              required={true}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleReconcileProject}
            variant="contained"
            startIcon={<LinkIcon />}
            disabled={!selectedProject?.id}
            sx={{
              bgcolor: COLORS.PRIMARY,
              '&:hover': {
                bgcolor: COLORS.PRIMARY_DARK,
              }
            }}
          >
            Reconcile
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomProjectsReconciliation;
